import {
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Popover,
    Slider,
} from '@mui/material';
import type { SliderProps } from '@mui/material';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { UpDownArrowIcon } from '../UpDownArrowIcon';
import { DataSheetFilterProjectBudgetCurrency as ProjectBudgetCurrency } from '../../schema.graphql.ts';
import {
    useProjectBudgetFilterDescription,
    type ProjectBudgetFilterValue,
} from './useProjectBudgetFilterDescription.ts';

export type ProjectBudgetFilterProps = {
    value: ProjectBudgetFilterValue;
    onChange(value: ProjectBudgetFilterValue): void;
};

function indexOfOrNull(array: ReadonlyArray<unknown>, item: unknown): number {
    const index = array.indexOf(item);
    return index === -1 ? null : index;
}

export const ProjectBudgetFilter = (props: ProjectBudgetFilterProps) => {
    // todo We support EUR and USD as well in the backend. Technically we could support any currency,
    //      but there are precalculated values SEK, EUR and USD. For other currencies, we would have
    //      to recalculate to on of the supported currencies before applying the filter, and it may
    //      not be accurate.
    const currency = ProjectBudgetCurrency.Sek;

    const scale = [
        0,
        100_000,
        250_000,
        500_000,
        750_000,
        1_000_000,
        2_500_000,
        5_000_000,
        7_500_000,
        10_000_000,
        25_000_000,
        50_000_000,
        75_000_000,
        100_000_000,
        250_000_000,
        500_000_000,
        750_000_000,
        1_000_000_000,
        Infinity,
    ];
    const minValue = 0;
    const maxValue = scale.length - 1;
    const minDistance = 1;

    const { formatNumber, formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);

    const [value, setValue] = useState([
        indexOfOrNull(scale, props.value.from) ?? minValue,
        indexOfOrNull(scale, props.value.to) ?? maxValue,
    ]);
    const handleChange: SliderProps['onChange'] = (
        _event,
        newValue,
        activeThumb,
    ) => {
        if (!Array.isArray(newValue)) {
            return;
        }
        if (newValue[1] - newValue[0] < minDistance) {
            if (activeThumb === 0) {
                const clamped = Math.min(newValue[0], maxValue - minDistance);
                setValue([clamped, clamped + minDistance]);
            } else {
                const clamped = Math.max(newValue[1], minValue + minDistance);
                setValue([clamped - minDistance, clamped]);
            }
        } else {
            setValue(newValue);
        }
    };
    const calculateAmount = (value: number): number => {
        return scale[value];
    };
    const from = calculateAmount(value[0]);
    const to = calculateAmount(value[1]);

    const hasFromOrTo = from !== 0 || to !== Infinity;
    const displayValue = useProjectBudgetFilterDescription({
        currency,
        from: from === 0 ? null : from,
        to: to === Infinity ? null : to,
    });

    const label = formatMessage({
        defaultMessage: 'Project budget',
        description: 'Label for project budget filter',
    });
    const shrink = isOpen || hasFocus || hasFromOrTo;
    return (
        <>
            <FormControl
                fullWidth
                ref={ref}
                margin="normal"
                focused={isOpen || hasFocus}
                onFocus={() => {
                    setHasFocus(true);
                }}
                onBlur={() => {
                    setHasFocus(false);
                    if (isOpen) {
                        setTimeout(() => {
                            sliderRef.current
                                .getElementsByTagName('input')[0]
                                .focus();
                        }, 0);
                    }
                }}
                onKeyDown={(event) => {
                    if (
                        event.code.startsWith('Arrow') ||
                        event.code === 'Space' ||
                        event.code === 'Enter'
                    ) {
                        setIsOpen(true);
                    }
                }}
                onClick={() => setIsOpen(true)}
            >
                <InputLabel htmlFor="my-input" shrink={shrink}>
                    {label}
                </InputLabel>
                <OutlinedInput
                    readOnly
                    notched={shrink}
                    label={label}
                    startAdornment={
                        displayValue === '' ? null : (
                            <Chip label={displayValue} color="primary" />
                        )
                    }
                    endAdornment={
                        <UpDownArrowIcon
                            direction={isOpen ? 'up' : 'down'}
                            sx={(theme) => ({
                                color: theme.palette.text.secondary,
                            })}
                        />
                    }
                />
            </FormControl>
            <Popover
                open={isOpen}
                onClose={() => {
                    setIsOpen(false);
                    props.onChange({
                        currency,
                        from: from === 0 ? null : from,
                        to: to === Infinity ? null : to,
                    });
                }}
                anchorEl={ref.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            p: {
                                xs: 2,
                                md: 3,
                            },
                            width: ref.current?.offsetWidth,
                        },
                    },
                }}
            >
                <Slider
                    value={value}
                    min={minValue}
                    max={maxValue}
                    ref={sliderRef}
                    track={from === 0 && to === Infinity ? false : 'normal'}
                    scale={calculateAmount}
                    disableSwap
                    onChange={handleChange}
                    valueLabelDisplay="off"
                    getAriaLabel={() => 'Project budget range'}
                    getAriaValueText={(value) =>
                        `${currency} ${formatNumber(calculateAmount(value))}`
                    }
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            setIsOpen(false);
                        }
                    }}
                />
                <FormHelperText>
                    {formatMessage({
                        defaultMessage:
                            'Use the sliders to filter by project budget',
                    })}
                </FormHelperText>
            </Popover>
        </>
    );
};
